/**
 * Serviço centralizado para operações de sistema do CorelDRAW
 * @module SystemService
 * 
 * ⚠️ IMPORTANTE: Todas as operações de sistema DEVEM usar HMAC (sendActionGuarded)
 */

import { corelCommunication } from '../communication';
import { sendToTerminal } from '../../terminalLogger';
import { LogCategory } from '@/config/logging';
import { getActionTimeout } from '../timeoutService';
import type {
  AppStatusPayload,
  AppStatusResponse,
  HealthPongPayload,
  AppStatusOptions
} from './system.types';

// Cache para evitar logs duplicados de app-status
let lastAppStatusLogTime = 0;
const APP_STATUS_LOG_THROTTLE_MS = 2000; // 2 segundos entre logs

/**
 * Serviço centralizado para comunicação de sistema com o host C#
 */
export class SystemService {
  /**
   * Envia status da aplicação para o host C#
   * 
   * ⚠️ CRÍTICO: SEMPRE usa sendActionGuarded
   * Conforme contrato do backend C# (03/12/2025):
   * - EXIGE sessionToken na raiz da mensagem
   * - É ISENTA de HMAC (não envia data.auth)
   * 
   * @param payload Dados do status da aplicação
   * @param options Opções de configuração
   * @returns Promise com resposta (se waitForResponse=true) ou void
   */
  static async sendAppStatus(
    payload: AppStatusPayload,
    options?: AppStatusOptions
  ): Promise<AppStatusResponse | void> {
    const waitForResponse = options?.waitForResponse ?? false;
    const timeoutMs = options?.timeoutMs ?? 5000;

    if (!waitForResponse) {
      // Fire-and-forget mode (padrão) - log throttled
      try {
        corelCommunication.sendActionGuarded('app-status', payload);
        // Throttle de log para evitar spam
        const now = Date.now();
        if (now - lastAppStatusLogTime > APP_STATUS_LOG_THROTTLE_MS) {
          sendToTerminal('✅ [SYSTEM] app-status enviado', {
            apiOnline: payload.apiOnline
          }, LogCategory.CONNECTION);
          lastAppStatusLogTime = now;
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        sendToTerminal('❌ [SYSTEM] Erro em app-status', { error: errorMessage });
      }
      return;
    }

    // Wait for response mode
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const timeoutId = setTimeout(() => {
        sendToTerminal('⏱️ [SYSTEM] Timeout em sendAppStatus', {
          timeoutMs,
          elapsedMs: Date.now() - startTime
        });
        resolve({
          status: 'error',
          message: 'Timeout ao enviar app-status'
        });
      }, timeoutMs);

      try {
        const requestId = corelCommunication.sendActionGuarded(
          'app-status',
          payload,
          (response) => {
            clearTimeout(timeoutId);
            const elapsedMs = Date.now() - startTime;
            
            sendToTerminal('✅ [SYSTEM] sendAppStatus concluído', {
              status: response.status,
              elapsedMs
            });

            resolve(response as AppStatusResponse);
          }
        );

        if (!requestId) {
          clearTimeout(timeoutId);
          sendToTerminal('❌ [SYSTEM] Falha ao enviar comando app-status');
          resolve({
            status: 'error',
            message: 'Falha ao enviar comando app-status'
          });
        }
      } catch (error) {
        clearTimeout(timeoutId);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        sendToTerminal('❌ [SYSTEM] Exceção em sendAppStatus', { error: errorMessage });
        reject(error);
      }
    });
  }

  /**
   * Responde a health:ping do host C#
   * 
   * Esta é uma operação fire-and-forget (não aguarda resposta)
   * 
   * ⚠️ CONTRATO (05/12/2025): Conforme backend C#
   * - EXIGE sessionToken na raiz da mensagem
   * - É ISENTA de HMAC (não envia data.auth)
   * 
   * @param payload Dados do pong
   */
  static sendHealthPong(payload: HealthPongPayload): void {
    try {
      corelCommunication.sendActionGuarded('health:pong', payload);
      
      // Health pong é logado apenas em nível DEBUG (já filtrado pelo sendToTerminal)
      sendToTerminal('🏓 [SYSTEM] Health pong enviado (sem HMAC, com token)', {
        nonce: payload.nonce,
        apiOnline: payload.apiOnline
      }, LogCategory.HEALTH);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      sendToTerminal('❌ [SYSTEM] Erro em sendHealthPong', { error: errorMessage });
    }
  }

  /**
   * Notifica o host C# que o WebView está disponível
   * 
   * Esta é uma operação fire-and-forget (não aguarda resposta)
   * Usa sendActionGuarded para garantir HMAC quando necessário
   * 
   * @param payload Dados opcionais (geralmente vazio)
   */
  static notifyWebViewAvailable(payload: Record<string, any> = {}): void {
    try {
      // Payload vazio é comum para esta action
      const data = Object.keys(payload).length > 0 ? payload : {};
      
      corelCommunication.sendActionGuarded('notificar-webview-disponivel', data);
      
      sendToTerminal('✅ [SYSTEM] WebView disponível notificado', {
        hasPayload: Object.keys(data).length > 0
      }, LogCategory.CONNECTION);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      sendToTerminal('❌ [SYSTEM] Erro em notifyWebViewAvailable', { error: errorMessage });
    }
  }

  /**
   * Envia app-status de forma segura (com retry em caso de falha)
   *
   * @param payload Dados do status
   * @param maxRetries Número máximo de tentativas (padrão: 1 - sem retry)
   * @returns Promise<boolean> - true se enviado com sucesso
   */
  static async sendAppStatusSafe(
    payload: AppStatusPayload,
    maxRetries: number = 1
  ): Promise<boolean> {
    // Nota: Não logamos início aqui pois sendAppStatus já loga internamente

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await this.sendAppStatus(payload, {
          waitForResponse: true,
          timeoutMs: getActionTimeout('app-status') // timeout do painel admin
        });

        if (result && result.status === 'ok') {
          // Sucesso - sendAppStatus já logou o resultado
          return true;
        }

        if (attempt < maxRetries) {
          sendToTerminal('🔄 [SYSTEM] Retry sendAppStatus', {
            attempt,
            maxRetries
          });
          await new Promise(r => setTimeout(r, 500));
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);

        if (attempt >= maxRetries) {
          sendToTerminal('❌ [SYSTEM] sendAppStatus falhou após tentativas', {
            maxRetries,
            error: errorMessage
          });
          return false;
        }
        // Se ainda há tentativas, o loop continua sem log extra
      }
    }

    return false;
  }
}

/**
 * Função auxiliar para compatibilidade (fire-and-forget)
 * @deprecated Use SystemService.sendAppStatus() diretamente
 */
export const sendAppStatus = (payload: AppStatusPayload) => {
  return SystemService.sendAppStatus(payload, { waitForResponse: false });
};

/**
 * Função auxiliar para health pong
 * @deprecated Use SystemService.sendHealthPong() diretamente
 */
export const sendHealthPong = SystemService.sendHealthPong;

